import { Badge, Button, Card, Flex, Link, Separator, Text, TextField, Tooltip } from "@radix-ui/themes";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { useMemo } from "react";

import Switch from "@/components/shared/inputs/switch/Switch";

import { useIntegrations } from "@/api/integration/Integration.api";
import { useSlackNotifications } from "@/hooks/useSlackNotifications";
import { NotificationTriggerType, TRIGGER_DEFINITIONS } from "@/v2/entities/notifications/NotificationTriggers.model";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";

const channelPlaceholders: Record<NotificationTriggerType, string> = {
  TICKET_ANSWERED: "Enter channel ID (e.g., C1234567890)",
  TRACKER_CAPTURED: "Enter channel ID (e.g., C1234567890)",
  REGRESSION_TEST_SUCCEEDED: "Enter channel ID (e.g., C1234567890)",
  REGRESSION_TEST_FAILED: "Enter channel ID (e.g., C1234567890)",
  BAD_CSAT_SUBMITTED: "Enter channel ID (e.g., C1234567890)",
  HITL_APPROVAL_REQUESTED: "Enter channel ID for HITL approvals",
  USAGE_THRESHOLD_REACHED: "Enter channel ID for 80% usage alerts",
  DAILY_DIGEST: "Enter channel ID (e.g., C1234567890)",
};

export function Notifications() {
  const { rows, handleChannelIdChange, handleSwitchChange, isLoading, updatingTrigger } = useSlackNotifications();
  const { data: integrations } = useIntegrations();
  const billingEnabled = useFlag("billingEnabled", false);

  const slackConnected = useMemo(() => {
    return (integrations ?? []).some(
      (i) => i.type === "CHAT" && i.name === "slack" && i.status === "COMPLETED"
    );
  }, [integrations]);

  const filteredRows = useMemo(() => {
    return rows.filter((notification) => {
      if (notification.trigger === "USAGE_THRESHOLD_REACHED" && !billingEnabled) {
        return false;
      }
      return true;
    });
  }, [rows, billingEnabled]);

  return (
    <Flex direction="column" gap="6">
      <Flex direction="column" gap="2">
        <Flex justify="between" align="center">
          <Text size="6" weight="bold">Notifications</Text>
          {slackConnected ? (
            <Badge color="green" variant="soft" size="2">✓ Slack Connected</Badge>
          ) : (
            <Button size="2" onClick={() => (window.location.href = "/settings/data-sources")}>Connect Slack</Button>
          )}
        </Flex>
        <Text size="3" color="gray">
          Configure your notification preferences for various events
        </Text>
      </Flex>

      <Flex direction="column" gap="6">
        {isLoading ? (
          <Flex justify="center" py="8">
            <Text size="2" color="gray">Loading notification settings...</Text>
          </Flex>
        ) : (
          <Flex direction="column" gap="4">
            {filteredRows.map((notification) => (
              <Card key={notification.trigger} variant="surface" size="2">
                <Flex direction="column" gap="3" p="4">
                  <Flex justify="between" align="start" gap="4">
                    <Flex direction="column" gap="1" style={{ flex: 1 }}>
                      <Text size="3" weight="medium">{notification.title}</Text>
                      <Text size="2" color="gray">{notification.description}</Text>
                    </Flex>
                    <Switch
                      value={notification.enabled}
                      onChange={(checked: boolean) => handleSwitchChange(notification, checked)}
                      disabled={updatingTrigger === notification.trigger}
                    />
                  </Flex>

                  <Flex direction="column" gap="2">
                    <Flex direction="row" align="center" gap="2">
                      <Text size="2" weight="medium">Slack Channel ID</Text>
                      {notification.trigger === "USAGE_THRESHOLD_REACHED" && (
                        <Tooltip content="Configure Stripe alerts at 80% and enter the Slack channel ID that should receive the warning.">
                          <Flex align="center" gap="1" asChild>
                            <Text size="1" color="gray">
                              <InfoCircledIcon /> 80% usage
                            </Text>
                          </Flex>
                        </Tooltip>
                      )}
                    </Flex>
                    <TextField.Root
                      value={notification.channelId || ""}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChannelIdChange(notification.trigger, e.target.value)
                      }
                      disabled={!slackConnected}
                      placeholder={channelPlaceholders[notification.trigger as NotificationTriggerType]}
                    />
                  </Flex>
                </Flex>
              </Card>
            ))}
          </Flex>
        )}

        <Card>
          <Flex direction="column" gap="2">
            <Text size="2" weight="medium">How to find your Slack Channel ID</Text>
            <Separator size="4" />
            <Text size="1" color="gray">
              Need help finding your channel ID? {" "}
              <Link
                href="https://integrations.quack.ai/BKUZmzGN2Mmipf4UROTF/notifications/slack/find-a-slack-channel-id"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn how to get channel ID from Slack
              </Link>
            </Text>
            <Text size="1" color="gray">
              For usage alerts, make sure you’ve enabled the 80% threshold notification in your Stripe dashboard so alerts trigger in real time.
            </Text>
          </Flex>
        </Card>
      </Flex>
    </Flex>
  );
}
